import React, { MutableRefObject, PropsWithChildren, ReactPortal, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import { POPUP_NODE_CLASSNAME } from "shared/constants/common.constants";
import { AppDOM } from "shared/utils/app.utils";
import styles from "./Portal.module.scss";

type TPortalElement = Element;
type TPortalNode = TPortalElement | null | undefined;
type TContainerIdentifier = string | TPortalNode;
type TUsePortalNodeReturn = MutableRefObject<TPortalNode>;
type PortalProps = {
  onMount?: () => void;
  containerIdentifier?: TContainerIdentifier;
  style?: React.CSSProperties;
  className?: string;
  id?: string;
  prependToBody?: boolean;
  closePopup?: () => void;
};

const cssPropertiesToString = (cssProperties: React.CSSProperties) => {
  return Object.entries(cssProperties).reduce((acc, [prop, value]) => {
    acc = acc.concat(`${prop}: ${value};`);
    return acc;
  }, "");
};

const usePortalNode = (containerIdentifier?: TContainerIdentifier): TUsePortalNodeReturn => {
  const [_, setIsSelectorGrepped] = useState<boolean>(false);
  const portalNodeRef = useRef<TPortalNode>(null);
  const fallbackRef = useRef<TPortalNode>((containerIdentifier as TPortalNode) || null);

  useLayoutEffect(() => {
    if (typeof containerIdentifier === "string") {
      portalNodeRef.current = document.querySelector(containerIdentifier);
      setIsSelectorGrepped(true);
    }
  }, []);

  return typeof containerIdentifier === "string" ? portalNodeRef : fallbackRef;
};

export const Portal = ({
  className,
  id: portalId,
  style,
  prependToBody = false,
  containerIdentifier = AppDOM.querySelector<HTMLDivElement>(`.${POPUP_NODE_CLASSNAME}`),
  onMount,
  closePopup,
  children,
}: PropsWithChildren<PortalProps>): ReactPortal => {
  const portalNodeRef = usePortalNode(containerIdentifier);
  const [dynamicPortalNode] = useState<TPortalElement>(document.createElement("div"));

  useLayoutEffect(() => {
    if (!portalNodeRef.current) {
      if (portalId) {
        dynamicPortalNode.setAttribute("id", portalId);
      }
      if (style) {
        dynamicPortalNode.setAttribute("style", cssPropertiesToString(style));
      }
      if (className) {
        dynamicPortalNode.classList.add(className);
      }
      if (prependToBody) {
        document.body.prepend(dynamicPortalNode);
      } else {
        document.body.appendChild(dynamicPortalNode);
      }
    }

    if (onMount) {
      onMount();
    }

    return () => {
      if (dynamicPortalNode && dynamicPortalNode.parentElement) {
        dynamicPortalNode.parentElement.removeChild(dynamicPortalNode);
      }
    };
  }, []);

  const onClickBackdrop = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    closePopup?.();
  };

  return createPortal(
    <div className={styles.popup} onClick={onClickBackdrop}>
      <div className={cn(styles.popupContainer, className)} onClick={(event) => event.stopPropagation()}>
        <div className={styles.popupChildren}>{children}</div>
      </div>
    </div>,
    portalNodeRef.current || dynamicPortalNode
  );
};

Portal.displayName = "Portal";
